<template>
  <div class="card p-shadow-app">
    <div class="card-header p-d-flex p-jc-between p-ai-center p-mb-4">
      <div>
        <h1 class="page-title"><i class="pi pi-truck p-mr-2"></i>Contas NACEX</h1>
        <small class="text-muted">Administração e gestão de credenciais e endereços NACEX</small>
      </div>
      <Button
        label="Adicionar Conta"
        icon="pi pi-plus"
        class="p-button-primary p-button-raised"
        @click="addNew"
        v-if="can('seeNacexAccounts')"
      />
    </div>

    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <ConfirmDialog></ConfirmDialog>

    <div style="height: 65vh" class="table-container">
      <DataTable
        :value="list"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        class="p-datatable-sm custom-table"
        sortMode="single"
        responsiveLayout="scroll"
        :scrollable="true"
        scrollHeight="flex"
      >
        <template #empty> <div class="p-py-4 text-center">Nenhuma conta NACEX configurada.</div> </template>
        <template #loading> A carregar as contas... Por favor aguarde. </template>

        <Column field="name" filterField="name" header="Nome da Empresa" sortable style="min-width: 15rem">
          <template #body="slotProps">
            <span class="clickable-item text-bold" @click="edit(slotProps.data)">
              {{ slotProps.data.name }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              v-model="filterModel.value"
              class="p-column-filter p-inputtext-sm"
              placeholder="Pesquisar por nome"
              @input="filterCallback()"
            />
          </template>
        </Column>

        <Column field="user" filterField="user" header="Utilizador (Username)" sortable style="min-width: 12rem">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              v-model="filterModel.value"
              class="p-column-filter p-inputtext-sm"
              placeholder="Pesquisar por utilizador"
              @input="filterCallback()"
            />
          </template>
        </Column>

        <Column field="agency_code" header="Cód. Agência" sortable style="min-width: 9rem"></Column>
        <Column field="client_code" header="Cód. Cliente" sortable style="min-width: 9rem"></Column>

        <Column field="email" header="Email" sortable style="min-width: 15rem"></Column>

        <Column field="phone" header="Telefone" style="min-width: 10rem"></Column>

        <Column field="city" header="Cidade" sortable style="min-width: 10rem"></Column>

        <Column header="Ações" style="min-width: 8rem; text-align: center;">
          <template #body="slotProps">
            <div class="p-d-flex p-jc-center" style="gap: 0.5rem">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-text p-button-success"
                v-tooltip.top="'Editar'"
                @click="edit(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger"
                v-tooltip.top="'Eliminar'"
                @click="confirmDelete(slotProps.data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Edit/Add Dialog -->
    <Dialog
      :visible.sync="form.show"
      :containerStyle="{ width: $isMobile() ? '100vw' : '50vw' }"
      :maximizable="true"
      :modal="true"
      @hide="hideForm"
      :header="form.id ? 'Editar Conta NACEX' : 'Nova Conta NACEX'"
      class="custom-dialog"
    >
      <form class="p-fluid p-grid p-formgrid p-mt-2" @submit.prevent="save">
        <!-- Credenciais -->
        <div class="p-col-12"><h3 class="section-title p-mb-3">Credenciais Web Service</h3></div>

        <div class="p-field p-col-12 p-md-6">
          <label for="user">Utilizador / API Username *</label>
          <InputText
            id="user"
            name="user"
            v-model="form.user"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('user') }"
            placeholder="Ex: 744700843"
          />
          <small v-if="errors.has('user')" class="p-error">O utilizador é obrigatório.</small>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="password">{{ form.id ? 'Nova Palavra-passe' : 'Palavra-passe *' }}</label>
          <InputText
            id="password"
            name="password"
            type="password"
            v-model="form.password"
            v-validate="form.id ? '' : 'required'"
            :class="{ 'p-invalid': errors.has('password') }"
            placeholder="Preencha apenas para alterar"
          />
          <small v-if="errors.has('password')" class="p-error">A palavra-passe é obrigatória.</small>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="agency_code">Código da Agência *</label>
          <InputText
            id="agency_code"
            name="agency_code"
            v-model="form.agency_code"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('agency_code') }"
            placeholder="Ex: 9992"
          />
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="client_code">Código do Cliente *</label>
          <InputText
            id="client_code"
            name="client_code"
            v-model="form.client_code"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('client_code') }"
            placeholder="Ex: 10045"
          />
        </div>

        <div class="p-col-12 p-mt-3"><h3 class="section-title p-mb-3">Detalhes da Empresa (Remetente)</h3></div>

        <div class="p-field p-col-12">
          <label for="name">Nome da Empresa *</label>
          <InputText
            id="name"
            name="name"
            v-model="form.name"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('name') }"
            placeholder="Ex: CIRCUIT CRAFTERS LDA"
          />
          <small v-if="errors.has('name')" class="p-error">O nome é obrigatório.</small>
        </div>

        <div class="p-field p-col-12">
          <label for="address">Morada *</label>
          <InputText
            id="address"
            name="address"
            v-model="form.address"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('address') }"
            placeholder="Rua, Número, Andar..."
          />
          <small v-if="errors.has('address')" class="p-error">A morada é obrigatória.</small>
        </div>

        <div class="p-field p-col-12 p-md-4">
          <label for="zip">Código Postal *</label>
          <InputText
            id="zip"
            name="zip"
            v-model="form.zip"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('zip') }"
            placeholder="Ex: 2635-244"
          />
          <small v-if="errors.has('zip')" class="p-error">O código postal é obrigatório.</small>
        </div>

        <div class="p-field p-col-12 p-md-4">
          <label for="city">Cidade *</label>
          <InputText
            id="city"
            name="city"
            v-model="form.city"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('city') }"
          />
          <small v-if="errors.has('city')" class="p-error">A cidade é obrigatória.</small>
        </div>

        <div class="p-field p-col-12 p-md-4">
          <label for="country">País *</label>
          <InputText
            id="country"
            name="country"
            v-model="form.country"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('country') }"
            placeholder="Ex: PT"
          />
          <small v-if="errors.has('country')" class="p-error">O país é obrigatório.</small>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="phone">Telefone de Contacto *</label>
          <InputText
            id="phone"
            name="phone"
            v-model="form.phone"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('phone') }"
          />
          <small v-if="errors.has('phone')" class="p-error">O telefone é obrigatório.</small>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="email">E-mail</label>
          <InputText
            id="email"
            name="email"
            type="email"
            v-model="form.email"
            v-validate="'email'"
            :class="{ 'p-invalid': errors.has('email') }"
          />
          <small v-if="errors.has('email')" class="p-error">E-mail inválido.</small>
        </div>
      </form>

      <template #footer>
        <Button
          label="Guardar"
          icon="pi pi-check"
          class="p-button-success p-button-sm"
          :disabled="dialogLoading"
          :loading="dialogLoading"
          @click="save"
        />
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-danger p-button-sm"
          :disabled="dialogLoading"
          @click="hideForm"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import nacexAccountsService from "@/services/nacex-accounts.service";
import { FilterMatchMode } from "primevue/api";

export default {
  name: "NacexAccounts",
  data() {
    return {
      loading: false,
      dialogLoading: false,
      list: [],
      form: {
        show: false,
        id: null,
        user: "",
        password: "",
        name: "",
        address: "",
        zip: "",
        city: "",
        phone: "",
        email: "",
        country: "PT",
        agency_code: "",
        client_code: "",
      },
      filters: {
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        user: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    };
  },
  async created() {
    await this.loadAccounts();
  },
  methods: {
    async loadAccounts() {
      this.loading = true;
      this.list = await nacexAccountsService.getAll();
      this.loading = false;
    },
    addNew() {
      this.form = {
        show: true,
        id: null,
        user: "",
        password: "",
        name: "",
        address: "",
        zip: "",
        city: "",
        phone: "",
        email: "",
        country: "PT",
        agency_code: "",
        client_code: "",
      };
      this.$nextTick(() => {
        this.$validator.reset();
      });
    },
    edit(data) {
      this.form = {
        show: true,
        id: data.id,
        user: data.user,
        password: "", // do not populate existing password
        name: data.name,
        address: data.address,
        zip: data.zip,
        city: data.city,
        phone: data.phone,
        email: data.email,
        country: data.country || "PT",
        agency_code: data.agency_code || "",
        client_code: data.client_code || "",
      };
      this.$nextTick(() => {
        this.$validator.reset();
      });
    },
    hideForm() {
      this.form.show = false;
      this.$validator.reset();
    },
    async save() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.dialogLoading = true;
      try {
        let result;
        const payload = {
          user: this.form.user,
          name: this.form.name,
          address: this.form.address,
          zip: this.form.zip,
          city: this.form.city,
          phone: this.form.phone,
          email: this.form.email,
          country: this.form.country,
          agency_code: this.form.agency_code,
          client_code: this.form.client_code,
        };

        if (this.form.password) {
          payload.password = this.form.password;
        }

        if (this.form.id) {
          result = await nacexAccountsService.update(this.form.id, payload);
        } else {
          result = await nacexAccountsService.create(payload);
        }

        if (result && !result.error) {
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Conta NACEX guardada com sucesso!",
            life: 3000,
          });
          this.form.show = false;
          await this.loadAccounts();
        } else {
          const detail = result?.error || "Erro ao guardar a conta.";
          this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: detail,
            life: 5000,
          });
        }
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Ocorreu um erro inesperado.",
          life: 5000,
        });
      } finally {
        this.dialogLoading = false;
      }
    },
    confirmDelete(data) {
      this.$confirm.require({
        message: `Tem a certeza que deseja eliminar a conta "${data.name}"?`,
        header: "Confirmar Eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Eliminar",
        rejectLabel: "Cancelar",
        acceptClass: "p-button-danger",
        accept: async () => {
          this.loading = true;
          const result = await nacexAccountsService.delete(data.id);
          this.loading = false;

          if (result && !result.error) {
            this.$toast.add({
              severity: "success",
              summary: "Sucesso",
              detail: "Conta NACEX eliminada com sucesso!",
              life: 3000,
            });
            await this.loadAccounts();
          } else {
            const detail = result?.error || "Erro ao eliminar a conta.";
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: detail,
              life: 5000,
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.custom-card {
  padding: 2rem;
  border-radius: 12px;
  background-color: #ffffff;
}

.page-title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
}

.text-muted {
  color: #7f8c8d;
}

.section-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #34495e;
  border-bottom: 2px solid #ecf0f1;
  padding-bottom: 0.5rem;
}

.clickable-item {
  color: #3498db;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
}

.clickable-item:hover {
  color: #2980b9;
  text-decoration: underline;
}

.text-bold {
  font-weight: 600;
}

.table-container {
  margin-top: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.custom-table >>> .p-datatable-thead > tr > th {
  background-color: #f8f9fa;
  color: #495057;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
}

.custom-dialog >>> .p-dialog-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}
</style>
